import { Button } from 'primereact/button';
import { InputMask, InputMaskChangeEvent, InputMaskCompleteEvent } from 'primereact/inputmask';
import React, { MutableRefObject, useEffect, useRef, useState } from 'react';
import { MdDomain, MdGroups } from 'react-icons/md';
import '../../styles/pages/components/InputCpfCnpj.css';
import { Toast } from 'primereact/toast';

type PartialEvent = {value: string|null|undefined}
type InputCpfCnpjProps = {
  value: string;
  setValue: Function;
  externalMode?: InputMode;
  onChange?: (e: InputMaskChangeEvent) => void;
  onModeChange?: (mode: InputMode) => void;
  onComplete?: (e: InputMaskCompleteEvent|PartialEvent) => void;
  includeButton?: boolean;
  label?: string;
  labelIcon?: React.ReactNode;
  labelStyle?: React.CSSProperties;
  toast?: MutableRefObject<Toast | null>;
  id?: string;
  floatLabel?: string;
};
type InputMode = 'cpf' | 'cnpj';
const masks = {
  cpf: '999.999.999-99',
  cnpj: '99.999.999/9999-99',
}
const icons = {
  cpf: <MdGroups />,
  cnpj: <MdDomain />,
}
const getDigits = (value: string): string => {
  return value.replace(/\D/g, '');
}

const InputCpfCnpj: React.FC<InputCpfCnpjProps> = (props: InputCpfCnpjProps) => {
  const includeButton = props.includeButton ?? true; // Default value for includeButton
  const [mode, setMode] = useState<InputMode>(props.externalMode || 'cpf');
  const inputRef = useRef<InputMask>(null)
  const handleChange = (e: InputMaskChangeEvent) => {
    if (!e.originalEvent) return
    switch (e.originalEvent.type){
      case 'keypress':
        const originalEvent = e.originalEvent as unknown as KeyboardEvent;
        if (/\D/.test(originalEvent.key)) break // Ignore non-digit keys
        if (e.value === props.value && mode === 'cpf'){
          const digits = getDigits(props.value);
          props.setValue(`${digits.slice(0,2)}.${digits.slice(2,5)}.${digits.slice(5,8)}/${digits.slice(8,11)}${originalEvent.key}`);
        } else {
          props.setValue(e.value || '');
        }
        break
      case 'paste':
        navigator.clipboard.readText().then((text) => {
          const trimmed = text.trim();
          const digits = trimmed.replace(/\D/g, '');
          if (digits.length === 11) {
            setMode('cpf');
            props.setValue(`${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6, 9)}-${digits.slice(9, 11)}`);
          } else if (digits.length === 14) {
            setMode('cnpj');
            props.setValue(`${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}/${digits.slice(8, 12)}-${digits.slice(12, 14)}`);
          } else {
            props.toast?.current?.show({ severity: 'error', summary: 'Erro', detail: 'CPF ou CNPJ inválido.' });
          }
        })
        break
      default:
        props.setValue(e.value || '');
        break
    }
    //console.log('InputCpfCnpj.handleChange', e);
    props.onChange?.(e);
  }
  const getClassName = () => {
    return ('inputcpfcnpj ' + (includeButton ? 'inputcpfcnpj-hasbutton' : '')+
    (props.label || props.labelIcon ? ' inputcpfcnpj-haslabel' : '')).trim();
  }
  useEffect(() => {
    if (props.onModeChange){
      props.onModeChange(mode);
    }
    const digits = getDigits(props.value);
    if (mode === 'cpf' && digits.length === 11) {
      props.onComplete&&props.onComplete({value: props.value})
    }
  }, [mode])
  useEffect(() => {
    const digits = getDigits(props.value);
    if (digits.length > 11 && mode === 'cpf') {
      setMode('cnpj');
    }
  }, [props.value]);
  useEffect(()=>{
    if (props.externalMode) {
      setMode(props.externalMode);
    }
  }, [props.externalMode]);
  return <div className={getClassName()}>
    {(props.label || props.labelIcon) && <span className='inputcpfcnpj-label' style={props.labelStyle}>
      <div>
        {props.labelIcon && props.labelIcon}
        {props.label && <b>{props.label}</b>}
      </div>
    </span>}
    <span className='p-float-label inputcpfcnpj-input'>
      <InputMask autoClear={false} mask={masks[mode]} value={props.value} onChange={handleChange}
      id={props.id} onComplete={props.onComplete} ref={inputRef} />
      {props.floatLabel && <label htmlFor={props.id}>{props.floatLabel}</label>}
    </span>
    {includeButton&&<Button icon={icons[mode]} className='inputcpfcnpj-button'
    onClick={()=>{mode==='cpf'?setMode('cnpj'):setMode('cpf')}} />}
  </div>
};

export default InputCpfCnpj;